import type { BenchReport } from "./report.ts";
import { machineWarning } from "./renderer-warning.ts";

/**
 * The benchmark's panel (P2.0-T4): a small box over the page's top right corner with a free-text label for the run, a
 * Start button that waits for the world to be shown, the step running, and at the end the results table with "Copy
 * results", which copies the report's text (machine block, table, slowest chunks and the JSON record); a browser that
 * refuses the clipboard leaves the text selected in a box to copy by hand. Over the table a line warns when the run
 * was drawn by integrated graphics or in software (`renderer-warning.ts`), the report's own first line. Under the table a line says what became of
 * the report the page posts to the dev server (P2.0-T8): the saved file's name, or "Not saved:" and why. It is a
 * developer tool with no design frame, drawn in plain page elements apart from the HUD.
 */

/**
 * What the panel tells its owner and is told.
 */
export interface BenchPanel {
  readonly label: () => string;
  setReady(ready: boolean): void;
  setStatus(text: string): void;
  showResults(report: BenchReport, text: string): void;
  showSaved(text: string): void;
}

/**
 * Makes an element with inline styles and text.
 *
 * @param tag The tag.
 * @param style The inline style.
 * @param text Its text.
 * @returns The element.
 */
function element<K extends keyof HTMLElementTagNameMap>(tag: K, style: string, text = ""): HTMLElementTagNameMap[K] {
  const made = document.createElement(tag);
  made.setAttribute("style", style);
  made.textContent = text;
  return made;
}

/**
 * Mounts the panel.
 *
 * @param host Where it mounts.
 * @param initialLabel The label the URL carried, or "".
 * @param routeId The route's id, or an error to show in place of the Start button.
 * @param onStart Called when Start is pressed.
 * @returns The panel.
 */
export function mountBenchPanel(host: HTMLElement, initialLabel: string, routeId: { readonly id: string } | { readonly error: string }, onStart: () => void): BenchPanel {
  const box = element("section", "position:fixed;top:8px;right:8px;z-index:1000;max-width:min(96vw,980px);max-height:90vh;overflow:auto;background:#101418ee;color:#e8e4d8;font:12px/1.4 monospace;padding:10px;border:1px solid #445;border-radius:4px");
  box.setAttribute("aria-label", "Benchmark");
  box.dataset["bench"] = "panel";
  box.append(element("strong", "display:block;margin-bottom:6px", "Benchmark"));
  const status = element("p", "margin:4px 0");
  if ("error" in routeId) {
    status.textContent = routeId.error;
    box.append(status);
    host.append(box);
    return { label: () => "", setReady: () => undefined, setStatus: (text) => void (status.textContent = text), showResults: () => undefined, showSaved: () => undefined };
  }
  const labelRow = element("label", "display:block;margin:4px 0", "Label ");
  const label = element("input", "width:16em;font:inherit");
  label.type = "text";
  label.placeholder = "e.g. vivobook-battery";
  label.value = initialLabel;
  label.dataset["bench"] = "label";
  labelRow.append(label);
  const start = element("button", "font:inherit;margin:4px 0", `Start route ${routeId.id}`);
  start.type = "button";
  start.disabled = true;
  start.dataset["bench"] = "start";
  start.addEventListener("click", () => {
    start.disabled = true;
    label.disabled = true;
    onStart();
  });
  status.textContent = "Waiting for the world to show.";
  box.append(labelRow, start, status);
  host.append(box);
  let started = false;
  const saved = element("p", "margin:4px 0", "Saving the report on the dev server.");
  saved.dataset["bench"] = "saved";
  return {
    label: () => label.value.trim(),
    setReady: (ready) => {
      if (!started && ready) {
        start.disabled = false;
        status.textContent = "Ready. Keep this tab in front and the window still while it runs.";
      }
    },
    setStatus: (text) => {
      started = true;
      status.textContent = text;
    },
    showResults: (report, text) => {
      status.textContent = `Done: ${report.rows.length} rows.`;
      const table = element("table", "border-collapse:collapse;margin:6px 0");
      const headers = ["row", "fps", "p50", "p95", "p99", "longest", ">33ms", "crossings", ">33ms at crossings", "props", "creatures"];
      const head = element("tr", "");
      for (const header of headers) {
        head.append(element("th", "text-align:left;padding:1px 6px;border-bottom:1px solid #556", header));
      }
      table.append(head);
      for (const row of report.rows) {
        const line = element("tr", "");
        for (const cell of [row.id, row.fps, row.p50, row.p95, row.p99, row.longest, row.over33, row.crossings, row.crossingOver33, row.props, row.creatures]) {
          line.append(element("td", "padding:1px 6px", String(cell)));
        }
        table.append(line);
      }
      const copy = element("button", "font:inherit;margin:4px 0", "Copy results");
      copy.type = "button";
      copy.dataset["bench"] = "copy";
      const fallback = element("textarea", "display:block;width:100%;height:8em;font:inherit;margin-top:4px");
      fallback.readOnly = true;
      fallback.value = text;
      fallback.dataset["bench"] = "text";
      copy.addEventListener("click", () => {
        const done = (): void => void (copy.textContent = "Copied");
        const manual = (): void => {
          fallback.focus();
          fallback.select();
          copy.textContent = "Select and copy the text below";
        };
        try {
          navigator.clipboard.writeText(text).then(done, manual);
        } catch {
          manual();
        }
      });
      const warning = machineWarning(report.machine);
      if (warning !== null) {
        const line = element("p", "margin:6px 0;padding:4px 6px;max-width:72ch;border:1px solid #c9a227;color:#ffd866", warning);
        line.setAttribute("role", "alert");
        line.dataset["bench"] = "warning";
        box.append(line);
      }
      box.append(table, saved, copy, fallback);
    },
    showSaved: (text) => {
      saved.textContent = text;
    },
  };
}
